import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { isLightTheme } from './config';

// =============================================================================
// ROASTBREW - CALENDAR PAGE
// =============================================================================
const TIER_COLORS = {
  free: { bg: '#F5F5F0', bgAlt: '#EEEEE8', accent: '#8B8B7A', accentGlow: 'rgba(139,139,122,0.3)', text: '#1A1A1A', textSecondary: '#4A4A42', muted: '#6B6B5F', cardBg: '#FFFFFF' },
  pro: { bg: '#1A1A1A', bgAlt: '#0E0E0E', accent: '#C9A962', accentGlow: 'rgba(201,169,98,0.4)', text: '#F5F5F0', textSecondary: '#C0C0B8', muted: '#A0A090', cardBg: '#222222' },
  pro_plus: { bg: '#0D0D0D', bgAlt: '#050505', accent: '#E8D5A3', accentGlow: 'rgba(232,213,163,0.35)', text: '#F5F5F0', textSecondary: '#D0D0C8', muted: '#B0B0A0', cardBg: '#181818' },
};

// =============================================================================
// SAMPLE DATA — Full year keyed by "YYYY-MM-DD"
// =============================================================================
const MONTHS_FULL = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const EVT = (title, time, endTime, color, desc, course) => ({ title, time, endTime, color, desc, course });

const YEARLY_EVENTS = {
  // JANUARY
  '2026-01-05': [EVT('New Year Strategy Session','10:00 AM','12:00 PM','#667eea','Kickoff planning for Q1 objectives and team alignment.','Brand Strategy')],
  '2026-01-12': [EVT('Leadership Workshop','2:00 PM','4:00 PM','#f5576c','Workshop on adaptive leadership in changing markets.','Leadership')],
  '2026-01-19': [EVT('Innovation Sprint','9:00 AM','11:00 AM','#4facfe','Rapid ideation session on new product concepts.','Innovation')],
  '2026-01-22': [EVT('Portfolio Review','11:00 AM','12:00 PM','#fa709a','Q4 portfolio assessment with advisor.')],
  '2026-01-28': [EVT('Study Group','4:00 PM','5:30 PM','#43e97b','Case study review for Module 1.')],
  // FEBRUARY
  '2026-02-02': [EVT('Brand Deep Dive','1:00 PM','3:00 PM','#667eea','Competitive landscape analysis for luxury sector.','Brand Strategy')],
  '2026-02-09': [EVT('Executive Coaching','10:00 AM','11:00 AM','#f093fb','One-on-one coaching session on presence.'),EVT('Financial Review','10:30 AM','11:30 AM','#C9A962','Monthly financial performance review.')],
  '2026-02-14': [EVT('Valentine Strategy Brunch','11:00 AM','1:00 PM','#fa709a','Themed networking brunch with guest speaker.')],
  '2026-02-18': [EVT('Design Thinking Lab','2:00 PM','4:00 PM','#4facfe','Prototyping workshop on customer journey.','Innovation')],
  '2026-02-25': [EVT('Midterm Review','9:00 AM','11:00 AM','#f5576c','Mid-module assessment and feedback.','Leadership')],
  // MARCH
  '2026-03-03': [EVT('Executive Presence Review','10:00 AM','11:30 AM','#667eea','Review quarterly leadership metrics and presence framework.','Executive Presence')],
  '2026-03-07': [EVT('Strategy Workshop','2:00 PM','4:00 PM','#f5576c','Competitive positioning in luxury markets.','Brand Strategy'),EVT('Financial Review','2:30 PM','3:30 PM','#C9A962','Mid-quarter financial review.')],
  '2026-03-10': [EVT('Leadership Lecture','9:00 AM','10:30 AM','#4facfe','Foundations of authority — Part 2.','Executive Presence'),EVT('Coaching Call','9:30 AM','10:00 AM','#f093fb','Executive coaching on Q1 goals.'),EVT('Study Group','4:00 PM','5:30 PM','#43e97b','Case studies from Module 3.')],
  '2026-03-14': [EVT('Portfolio Review','11:00 AM','12:00 PM','#fa709a','One-on-one portfolio assessment.')],
  '2026-03-15': [EVT('Brand Strategy Session','9:00 AM','10:30 AM','#667eea','Premium brand positioning case studies.','Brand Strategy'),EVT('Design Thinking Lab','9:30 AM','11:00 AM','#f093fb','Customer onboarding design sprint.','Innovation'),EVT('Executive Presence Review','1:00 PM','2:00 PM','#4facfe','Quarterly presence metrics review.','Executive Presence'),EVT('Study Group','4:00 PM','5:30 PM','#43e97b','Module 3 case study feedback.')],
  '2026-03-18': [EVT('Innovation Lab','3:00 PM','5:00 PM','#C9A962','Design thinking and prototyping workshop.','Innovation')],
  '2026-03-21': [EVT('Midterm Prep','10:00 AM','12:00 PM','#f093fb','Faculty-led review of key frameworks.','Leadership'),EVT('Office Hours','10:30 AM','11:30 AM','#667eea','Open faculty office hours.')],
  '2026-03-24': [EVT('Guest Lecture','6:00 PM','7:30 PM','#4facfe','Fortune 500 exec on disruptive innovation.'),EVT('Networking Dinner','8:00 PM','10:00 PM','#C9A962','Dinner with guest speaker.')],
  '2026-03-28': [EVT('Final Presentation','9:00 AM','11:00 AM','#43e97b','Executive Presence capstone presentations.','Executive Presence'),EVT('Peer Review Panel','9:30 AM','10:30 AM','#fa709a','Peer evaluation of capstone work.'),EVT('Celebration Brunch','11:30 AM','1:00 PM','#C9A962','Series completion celebration.')],
  // APRIL
  '2026-04-01': [EVT('Q2 Kickoff','9:00 AM','11:00 AM','#667eea','Quarter 2 strategy and goal setting.','Brand Strategy')],
  '2026-04-06': [EVT('Marketing Masterclass','1:00 PM','3:00 PM','#f5576c','Digital marketing for premium brands.','Brand Strategy')],
  '2026-04-10': [EVT('Innovation Lab','10:00 AM','12:00 PM','#4facfe','AI-driven product development workshop.','Innovation'),EVT('Coaching Session','10:30 AM','11:30 AM','#f093fb','Leadership presence coaching.')],
  '2026-04-15': [EVT('Tax Day Study Group','2:00 PM','3:30 PM','#43e97b','Financial literacy module review.')],
  '2026-04-20': [EVT('Guest Speaker Series','6:00 PM','7:30 PM','#C9A962','Industry leader on sustainable luxury.')],
  '2026-04-27': [EVT('Portfolio Workshop','9:00 AM','11:00 AM','#fa709a','Building a compelling leadership portfolio.')],
  // MAY
  '2026-05-04': [EVT('Strategy Retreat','9:00 AM','4:00 PM','#667eea','Full-day strategy immersion.','Brand Strategy')],
  '2026-05-08': [EVT('Peer Mentorship','2:00 PM','3:00 PM','#f093fb','Cross-cohort mentorship pairing.')],
  '2026-05-13': [EVT('Innovation Challenge','10:00 AM','12:00 PM','#4facfe','48-hour innovation challenge kickoff.','Innovation')],
  '2026-05-18': [EVT('Leadership Summit','9:00 AM','5:00 PM','#f5576c','Annual leadership summit — all cohorts.','Leadership')],
  '2026-05-22': [EVT('Financial Planning','11:00 AM','12:30 PM','#C9A962','Personal financial strategy session.')],
  '2026-05-29': [EVT('End of Module Review','10:00 AM','12:00 PM','#fa709a','Module 4 comprehensive review.')],
  // JUNE
  '2026-06-01': [EVT('Summer Intensive Begins','9:00 AM','10:00 AM','#667eea','Summer program orientation.')],
  '2026-06-08': [EVT('Brand Lab','1:00 PM','3:00 PM','#f5576c','Luxury brand building workshop.','Brand Strategy')],
  '2026-06-12': [EVT('Executive Coaching','10:00 AM','11:00 AM','#f093fb','Mid-year coaching review.'),EVT('Study Group','4:00 PM','5:30 PM','#43e97b','Summer reading discussion.')],
  '2026-06-18': [EVT('Innovation Demo Day','2:00 PM','5:00 PM','#4facfe','Present innovation challenge results.','Innovation')],
  '2026-06-25': [EVT('Networking Gala','7:00 PM','10:00 PM','#C9A962','Summer networking gala with industry leaders.')],
  // JULY
  '2026-07-06': [EVT('Leadership Intensive','9:00 AM','12:00 PM','#667eea','Three-day leadership intensive begins.','Leadership')],
  '2026-07-07': [EVT('Leadership Intensive Day 2','9:00 AM','12:00 PM','#667eea','Continued leadership intensive.','Leadership')],
  '2026-07-08': [EVT('Leadership Intensive Day 3','9:00 AM','12:00 PM','#667eea','Final day of leadership intensive.','Leadership')],
  '2026-07-14': [EVT('Strategy Workshop','2:00 PM','4:00 PM','#f5576c','Mid-year strategy recalibration.','Brand Strategy')],
  '2026-07-21': [EVT('Portfolio Review','11:00 AM','12:00 PM','#fa709a','Mid-year portfolio assessment.')],
  '2026-07-28': [EVT('Guest Lecture','6:00 PM','7:30 PM','#4facfe','Tech entrepreneur on disruption.')],
  // AUGUST
  '2026-08-03': [EVT('Innovation Sprint','9:00 AM','11:00 AM','#4facfe','Fall product concept development.','Innovation')],
  '2026-08-10': [EVT('Brand Strategy Review','1:00 PM','3:00 PM','#667eea','Comprehensive brand audit.','Brand Strategy')],
  '2026-08-17': [EVT('Study Group','4:00 PM','5:30 PM','#43e97b','Module 6 preparation.'),EVT('Coaching Call','4:00 PM','5:00 PM','#f093fb','Goal realignment session.')],
  '2026-08-24': [EVT('Financial Workshop','10:00 AM','12:00 PM','#C9A962','Investment strategy for professionals.')],
  '2026-08-31': [EVT('Fall Preview','2:00 PM','3:30 PM','#f5576c','Preview of fall curriculum and events.')],
  // SEPTEMBER
  '2026-09-07': [EVT('Fall Semester Launch','9:00 AM','11:00 AM','#667eea','Fall kickoff and cohort introductions.')],
  '2026-09-10': [EVT('Executive Presence II','10:00 AM','11:30 AM','#4facfe','Advanced presence techniques.','Executive Presence')],
  '2026-09-15': [EVT('Strategy Deep Dive','1:00 PM','3:00 PM','#f5576c','Advanced competitive strategy.','Brand Strategy'),EVT('Mentorship Pairing','3:00 PM','4:00 PM','#f093fb','New mentorship assignments.')],
  '2026-09-22': [EVT('Innovation Lab','2:00 PM','4:00 PM','#C9A962','AI and automation workshop.','Innovation')],
  '2026-09-28': [EVT('Guest Lecture','6:00 PM','7:30 PM','#fa709a','Award-winning CEO on resilience.')],
  // OCTOBER
  '2026-10-05': [EVT('Leadership Forum','9:00 AM','12:00 PM','#667eea','Quarterly leadership forum.','Leadership')],
  '2026-10-12': [EVT('Brand Workshop','1:00 PM','3:00 PM','#f5576c','Personal branding workshop.','Brand Strategy')],
  '2026-10-15': [EVT('Midterm Exams','9:00 AM','12:00 PM','#fa709a','Fall midterm examinations.'),EVT('Study Group','2:00 PM','4:00 PM','#43e97b','Last-minute midterm prep.')],
  '2026-10-20': [EVT('Innovation Showcase','2:00 PM','5:00 PM','#4facfe','Fall innovation project showcase.','Innovation')],
  '2026-10-28': [EVT('Halloween Networking','7:00 PM','10:00 PM','#C9A962','Themed networking event.')],
  // NOVEMBER
  '2026-11-02': [EVT('Q4 Strategy','10:00 AM','12:00 PM','#667eea','Q4 planning and year-end objectives.','Brand Strategy')],
  '2026-11-09': [EVT('Executive Coaching','10:00 AM','11:00 AM','#f093fb','Year-end coaching session.')],
  '2026-11-16': [EVT('Leadership Lecture','2:00 PM','3:30 PM','#4facfe','Legacy and long-term vision.','Leadership')],
  '2026-11-20': [EVT('Portfolio Final Review','11:00 AM','1:00 PM','#fa709a','Final portfolio presentations.'),EVT('Peer Feedback','1:30 PM','3:00 PM','#43e97b','Structured peer feedback session.')],
  '2026-11-30': [EVT('Giving Thanks Dinner','6:00 PM','9:00 PM','#C9A962','Annual gratitude dinner with cohort.')],
  // DECEMBER
  '2026-12-03': [EVT('Year in Review','10:00 AM','12:00 PM','#667eea','Comprehensive year-end review.','Brand Strategy')],
  '2026-12-08': [EVT('Final Exams','9:00 AM','12:00 PM','#f5576c','Fall semester final examinations.')],
  '2026-12-10': [EVT('Innovation Awards','2:00 PM','4:00 PM','#4facfe','Annual innovation award ceremony.','Innovation')],
  '2026-12-15': [EVT('Graduation Ceremony','10:00 AM','12:00 PM','#C9A962','2026 cohort graduation ceremony.'),EVT('Celebration Gala','7:00 PM','11:00 PM','#f093fb','Year-end black tie gala.')],
  '2026-12-18': [EVT('Holiday Study Group','2:00 PM','3:30 PM','#43e97b','Light holiday-themed review session.')],
};

// =============================================================================
// DATE UTILITIES
// =============================================================================
const DAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAYS_LETTER = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const DAY_NAMES_FULL = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

const TODAY_DATE = new Date(2026, 2, 15); // March 15, 2026
const TODAY_KEY = '2026-03-15';

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year, month) {
  return new Date(year, month, 1).getDay();
}

function dateKey(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function buildMonthCells(year, month) {
  const firstDay = getFirstDayOfMonth(year, month);
  const daysInMonth = getDaysInMonth(year, month);
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function getEventsForDate(year, month, day) {
  return YEARLY_EVENTS[dateKey(year, month, day)] || [];
}

// Build week events from yearly data for a given week start
function getWeekEvents(year, month, startDay) {
  const events = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(year, month, startDay + i);
    const key = dateKey(d.getFullYear(), d.getMonth(), d.getDate());
    const dayEvts = YEARLY_EVENTS[key] || [];
    dayEvts.forEach(ev => {
      events.push({
        day: i,
        title: ev.title,
        start: timeToDecimal(ev.time),
        duration: timeToDecimal(ev.endTime) - timeToDecimal(ev.time),
        color: ev.color,
        desc: ev.desc,
        course: ev.course,
      });
    });
  }
  return events;
}

function getWeekEventsFromDate(startDate) {
  const events = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(startDate);
    d.setDate(d.getDate() + i);
    const key = dateKey(d.getFullYear(), d.getMonth(), d.getDate());
    const dayEvts = YEARLY_EVENTS[key] || [];
    dayEvts.forEach(ev => {
      events.push({
        day: i,
        title: ev.title,
        start: timeToDecimal(ev.time),
        duration: timeToDecimal(ev.endTime) - timeToDecimal(ev.time),
        color: ev.color,
        desc: ev.desc,
        course: ev.course,
        _date: d,
      });
    });
  }
  return events;
}

// =============================================================================
// OVERLAP RESOLUTION
// =============================================================================
function resolveOverlaps(events) {
  if (!events.length) return [];
  const sorted = events.map((e, i) => ({ ...e, _idx: i })).sort((a, b) => {
    const aStart = a.start !== undefined ? a.start : 0;
    const bStart = b.start !== undefined ? b.start : 0;
    if (aStart !== bStart) return aStart - bStart;
    const aDur = a.duration !== undefined ? a.duration : 1;
    const bDur = b.duration !== undefined ? b.duration : 1;
    return bDur - aDur;
  });

  // Build overlap groups (connected components)
  const groups = [];
  let currentGroup = [sorted[0]];
  let groupEnd = (sorted[0].start || 0) + (sorted[0].duration || 1);

  for (let i = 1; i < sorted.length; i++) {
    const ev = sorted[i];
    const evStart = ev.start !== undefined ? ev.start : 0;
    if (evStart < groupEnd) {
      currentGroup.push(ev);
      groupEnd = Math.max(groupEnd, evStart + (ev.duration || 1));
    } else {
      groups.push(currentGroup);
      currentGroup = [ev];
      groupEnd = evStart + (ev.duration || 1);
    }
  }
  groups.push(currentGroup);

  // Assign lanes within each group
  const result = new Array(events.length);
  for (const group of groups) {
    const lanes = [];
    for (const ev of group) {
      const evStart = ev.start !== undefined ? ev.start : 0;
      const evEnd = evStart + (ev.duration || 1);
      let placed = false;
      for (let l = 0; l < lanes.length; l++) {
        if (lanes[l] <= evStart) {
          lanes[l] = evEnd;
          result[ev._idx] = { ...events[ev._idx], lane: l, totalLanes: 0 };
          placed = true;
          break;
        }
      }
      if (!placed) {
        result[ev._idx] = { ...events[ev._idx], lane: lanes.length, totalLanes: 0 };
        lanes.push(evEnd);
      }
    }
    const totalLanes = lanes.length;
    for (const ev of group) {
      result[ev._idx].totalLanes = totalLanes;
    }
  }
  return result;
}

// For month/overview: parse time string to decimal for overlap detection
function timeToDecimal(timeStr) {
  if (!timeStr) return 0;
  const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!match) return 0;
  let h = parseInt(match[1]);
  const m = parseInt(match[2]);
  const period = match[3].toUpperCase();
  if (period === 'PM' && h !== 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return h + m / 60;
}

export default function CalendarPage({
  isMobile = false,
  isTablet = false,
  isDesktop = true,
  sidebarOpen = true,
}) {
  const { profile } = useAuth();
  const currentTier = profile?.currentTier || 'free';
  const tier = currentTier || 'free';
  const [view, setView] = useState('overview');
  const [hoveredDay, setHoveredDay] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(2); // March = 2 (0-indexed)
  const [currentYear, setCurrentYear] = useState(2026);
  const [selectedDay, setSelectedDay] = useState(15); // Overview only
  const [showModal, setShowModal] = useState(false);
  const [showSlidePanel, setShowSlidePanel] = useState(false);
  const [viewingEvent, setViewingEvent] = useState(null);
  const [modalEditing, setModalEditing] = useState(false);
  const [expandedEvent, setExpandedEvent] = useState(null);
  const [modalStartTime, setModalStartTime] = useState('09:00');
  const [modalEndTime, setModalEndTime] = useState('10:00');
  const [modalDate, setModalDate] = useState(() => dateKey(2026, 2, 15));
  const [selectedColor, setSelectedColor] = useState('#667eea');
  const [showSendSchedule, setShowSendSchedule] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [sendConfirmed, setSendConfirmed] = useState(false);
  const [sendFormat, setSendFormat] = useState('ics');
  const [showDayDetail, setShowDayDetail] = useState(null);
  const [expandedDayEvent, setExpandedDayEvent] = useState(null);
  const [viewKey, setViewKey] = useState(0);

  // Derived calendar data
  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const monthCells = buildMonthCells(currentYear, currentMonth);
  const monthName = MONTHS_FULL[currentMonth];
  const todayDay = (currentYear === 2026 && currentMonth === 2) ? 15 : null; // Only show today indicator in the right month

  // Navigation
  const goToPrevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(y => y - 1); }
    else setCurrentMonth(m => m - 1);
  };
  const goToNextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(y => y + 1); }
    else setCurrentMonth(m => m + 1);
  };

  const EVENT_COLORS = [
    { hex: '#667eea', name: 'Indigo' },
    { hex: '#C9A962', name: 'Gold' },
    { hex: '#4facfe', name: 'Azure' },
    { hex: '#43e97b', name: 'Emerald' },
    { hex: '#f5576c', name: 'Coral' },
    { hex: '#f093fb', name: 'Orchid' },
    { hex: '#fa709a', name: 'Rose' },
    { hex: '#a18cd1', name: 'Lavender' },
  ];

  const c = TIER_COLORS[tier];
  const isLight = isLightTheme(tier);

  const borderLight = 'rgba(0,0,0,0.06)';
  const borderDark = 'rgba(255,255,255,0.06)';
  const border = isLight ? borderLight : borderDark;
  const subtleBg = isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)';
  const subtleBgHover = isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)';

  // Helpers for time conversion
  const decimalToTimeStr = (dec) => {
    const h = Math.floor(dec);
    const m = Math.round((dec - h) * 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };
  const decimalToDisplay = (dec) => {
    const h = Math.floor(dec);
    const m = Math.round((dec - h) * 60);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return m === 0 ? `${h12}:00 ${suffix}` : `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
  };

  const openModalWithTime = (dayIdx, startDecimal) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + dayIdx);
    setModalDate(dateKey(d.getFullYear(), d.getMonth(), d.getDate()));
    setModalStartTime(decimalToTimeStr(startDecimal));
    setModalEndTime(decimalToTimeStr(startDecimal + 1));
    setSelectedColor('#667eea');
    setShowModal(true);
  };

  useEffect(() => { setViewKey(k => k + 1); setShowDayDetail(null); setExpandedDayEvent(null); }, [view]);
  useEffect(() => { setExpandedEvent(null); }, [selectedDay]);
  useEffect(() => { setSelectedDay(1); }, [currentMonth, currentYear]);

  const dayOfWeek = DAY_NAMES_FULL[new Date(currentYear, currentMonth, selectedDay).getDay()];
  const selectedEvents = getEventsForDate(currentYear, currentMonth, selectedDay);

  // Week view uses a Date object so it can cross month boundaries
  const [weekStart, setWeekStart] = useState(() => new Date(2026, 2, 15));

  const getWeekDates = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(d.getDate() + i);
      dates.push(d);
    }
    return dates;
  };

  const navigateWeek = (direction) => {
    setWeekStart(prev => {
      const next = new Date(prev);
      next.setDate(next.getDate() + (direction * 7));
      return next;
    });
  };

  const navigateWeekByMonth = (direction) => {
    setWeekStart(prev => {
      const next = new Date(prev);
      next.setDate(next.getDate() + (direction * 28));
      return next;
    });
  };

  const formatWeekRange = () => {
    const dates = getWeekDates();
    const first = dates[0];
    const last = dates[6];
    const fm = MONTHS_FULL[first.getMonth()].slice(0, 3);
    const lm = MONTHS_FULL[last.getMonth()].slice(0, 3);
    if (first.getMonth() === last.getMonth() && first.getFullYear() === last.getFullYear()) {
      return `${fm} ${first.getDate()} — ${last.getDate()}, ${first.getFullYear()}`;
    }
    return `${fm} ${first.getDate()} — ${lm} ${last.getDate()}, ${last.getFullYear()}`;
  };

  // =========================================================================
  // CONTACTS & SEND SCHEDULE
  // =========================================================================
  const CONTACTS = [
    { id: 'c1', name: 'Pam Beesly', method: 'phone', detail: '+1 (555) 234-8901', initials: 'PB', color: '#667eea' },
    { id: 'c2', name: 'Johnny Rose', method: 'email', detail: 'johnny@rosevideo.com', initials: 'JR', color: '#C9A962' },
    { id: 'c3', name: 'Michael Scott', method: 'phone', detail: '+1 (555) 678-1234', initials: 'MS', color: '#f5576c' },
    { id: 'c4', name: 'Alexis Rose', method: 'email', detail: 'alexis@singlegal.com', initials: 'AR', color: '#f093fb' },
    { id: 'c5', name: 'David Kim', method: 'email', detail: 'david.kim@luxe.co', initials: 'DK', color: '#4facfe' },
  ];

  const toggleContact = (id) => {
    setSelectedContacts(prev =>
      prev.includes(id) ? prev.filter(c => c !== id) : [...prev, id]
    );
  };

  const handleSendSchedule = () => {
    setSendConfirmed(true);
    setTimeout(() => {
      setSendConfirmed(false);
      setShowSendSchedule(false);
      setSelectedContacts([]);
    }, 1800);
  };

  // =========================================================================
  // EVENT DETAIL MODAL (Display + Editing modes)
  // =========================================================================
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');

  const openEventEditing = (ev) => {
    setEditTitle(ev.title || '');
    setEditDesc(ev.desc || '');
    setModalStartTime(ev.time ? (() => { const d = timeToDecimal(ev.time); return decimalToTimeStr(d); })() : '09:00');
    setModalEndTime(ev.endTime ? (() => { const d = timeToDecimal(ev.endTime); return decimalToTimeStr(d); })() : '10:00');
    setModalDate(ev._day ? dateKey(ev._year || currentYear, ev._month !== undefined ? ev._month : currentMonth, ev._day) : dateKey(currentYear, currentMonth, 1));
    setSelectedColor(ev.color || '#667eea');
    setModalEditing(true);
  };

  const renderEventDetailModal = () => {
    if (!viewingEvent) return null;
    const ev = viewingEvent;
    const editing = modalEditing;
    return (
      <div onClick={() => { setViewingEvent(null); setModalEditing(false); }} style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: isLight ? 'rgba(245,245,240,0.88)' : 'rgba(0,0,0,0.75)',
        backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'calFadeInSimple 0.3s ease',
      }}>
        <div onClick={e => e.stopPropagation()} style={{
          width: '500px', maxWidth: '90vw', background: isLight ? c.cardBg : '#1E1E1E',
          border: `1px solid ${border}`, borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: isLight ? '0 32px 80px rgba(0,0,0,0.1)' : `0 32px 80px rgba(0,0,0,0.5), 0 0 40px ${c.accentGlow}`,
          animation: 'calScaleIn 0.4s cubic-bezier(0.4,0,0.2,1)',
        }}>
          {/* Color banner */}
          <div style={{
            height: '6px',
            background: `linear-gradient(90deg, ${editing ? selectedColor : ev.color}, ${editing ? selectedColor : ev.color}80)`,
            transition: 'background 0.3s ease',
          }} />

          <div style={{ padding: '32px 36px 36px' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: editing ? '20px' : '24px' }}>
              <div style={{ flex: 1, minWidth: 0, paddingRight: '16px' }}>
                {!editing && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <div style={{
                      width: '12px', height: '12px', borderRadius: '50%', background: ev.color, flexShrink: 0,
                      boxShadow: `0 0 10px ${ev.color}50`,
                    }} />
                    {ev.course && (
                      <span style={{
                        fontSize: '10px', fontFamily: '"DM Sans", sans-serif', fontWeight: '600',
                        color: isLight ? ev.color : c.accent,
                        letterSpacing: '0.08em', textTransform: 'uppercase',
                      }}>{ev.course}</span>
                    )}
                  </div>
                )}
                {editing ? (
                  <input value={editTitle} onChange={e => setEditTitle(e.target.value)} placeholder="Event title" style={{
                    width: '100%', padding: '10px 0', fontSize: '22px', fontWeight: '300',
                    background: 'transparent', border: 'none', color: c.text, outline: 'none',
                    borderBottom: `1px solid ${isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}`,
                    fontFamily: '"Cormorant Garamond", serif',
                  }} />
                ) : (
                  <h2 style={{
                    fontSize: '26px', fontWeight: '300', color: c.text,
                    fontFamily: '"Cormorant Garamond", serif', margin: 0, lineHeight: 1.2,
                  }}>{ev.title}</h2>
                )}
              </div>
              <button onClick={() => { setViewingEvent(null); setModalEditing(false); }} style={{
                width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
                background: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)',
                border: 'none', color: c.muted, cursor: 'pointer', fontSize: '18px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>×</button>
            </div>

            {/* Time + Date */}
            {editing ? (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ marginBottom: '12px' }}>
                  <label style={{ fontSize: '10px', fontFamily: '"DM Sans", sans-serif', color: c.muted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>Date</label>
                  <input type="date" value={modalDate} onChange={e => setModalDate(e.target.value)} style={{
                    width: '100%', padding: '10px 12px', fontSize: '13px', fontFamily: '"DM Sans", sans-serif',
                    background: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${border}`, borderRadius: '8px', color: c.text, outline: 'none',
                    colorScheme: isLight ? 'light' : 'dark',
                  }} />
                </div>
                <div style={{ display: 'flex', gap: '12px' }}>
                  {['Start', 'End'].map((label, i) => (
                    <div key={label} style={{ flex: 1 }}>
                      <label style={{ fontSize: '10px', fontFamily: '"DM Sans", sans-serif', color: c.muted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>{label}</label>
                      <input type="time" value={i === 0 ? modalStartTime : modalEndTime}
                        onChange={e => i === 0 ? setModalStartTime(e.target.value) : setModalEndTime(e.target.value)}
                        style={{
                          width: '100%', padding: '10px 12px', fontSize: '13px', fontFamily: '"DM Sans", sans-serif',
                          background: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.05)',
                          border: `1px solid ${border}`, borderRadius: '8px', color: c.text, outline: 'none',
                        }} />
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', borderRadius: '10px',
                  background: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'}`,
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c.accent} strokeWidth="1.5" strokeLinecap="round">
                    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                  </svg>
                  <span style={{ fontSize: '13px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500', color: c.text }}>{ev.time} — {ev.endTime}</span>
                </div>
                {ev._day && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', borderRadius: '10px',
                    background: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)'}`,
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c.accent} strokeWidth="1.5" strokeLinecap="round">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    <span style={{ fontSize: '13px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500', color: c.text }}>
                      {ev._day && DAY_NAMES_FULL[new Date(ev._year || currentYear, ev._month !== undefined ? ev._month : currentMonth, ev._day).getDay()]}, {MONTHS_FULL[ev._month !== undefined ? ev._month : currentMonth]} {ev._day}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Divider */}
            <div style={{ height: '1px', marginBottom: editing ? '16px' : '24px', background: isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)' }} />

            {/* Description */}
            {editing ? (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '10px', fontFamily: '"DM Sans", sans-serif', color: c.muted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>Description</label>
                <textarea value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Add details..." rows={3} style={{
                  width: '100%', padding: '12px', fontSize: '13px', fontFamily: '"DM Sans", sans-serif',
                  background: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${border}`, borderRadius: '8px', color: c.text, outline: 'none', resize: 'none',
                }} />
              </div>
            ) : (
              ev.desc && (
                <div style={{ marginBottom: '28px' }}>
                  <div style={{ fontSize: '10px', fontFamily: '"DM Sans", sans-serif', fontWeight: '600', color: c.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>Details</div>
                  <p style={{ fontSize: '14px', fontFamily: '"DM Sans", sans-serif', color: c.textSecondary, lineHeight: 1.8, margin: 0 }}>{ev.desc}</p>
                </div>
              )
            )}

            {/* Color picker — editing only */}
            {editing && (
              <div style={{ marginBottom: '24px' }}>
                <label style={{ fontSize: '10px', fontFamily: '"DM Sans", sans-serif', color: c.muted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '10px' }}>Color</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {EVENT_COLORS.map(clr => {
                    const isActive = selectedColor === clr.hex;
                    return (
                      <button key={clr.hex} onClick={() => setSelectedColor(clr.hex)} title={clr.name} style={{
                        width: '30px', height: '30px', borderRadius: '50%', border: 'none', cursor: 'pointer',
                        background: clr.hex, position: 'relative',
                        boxShadow: isActive ? `0 0 0 2px ${isLight ? c.cardBg : '#1E1E1E'}, 0 0 0 4px ${clr.hex}` : `0 2px 6px ${clr.hex}30`,
                        transform: isActive ? 'scale(1.1)' : 'scale(1)',
                        transition: 'all 0.2s ease',
                      }}>
                        {isActive && (
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }}>
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Course link — display only */}
            {!editing && ev.course && (
              <div style={{
                padding: '14px 18px', borderRadius: '12px',
                background: `${ev.color}08`, border: `1px solid ${ev.color}20`,
                display: 'flex', alignItems: 'center', gap: '12px',
                marginBottom: '28px', cursor: 'pointer',
              }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '10px',
                  background: `${ev.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isLight ? ev.color : c.text} strokeWidth="1.5">
                    <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" /><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" />
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: '13px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500', color: c.text }}>{ev.course}</div>
                  <div style={{ fontSize: '11px', fontFamily: '"DM Sans", sans-serif', color: c.muted }}>View in library</div>
                </div>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 'auto' }}>
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </div>
            )}

            {/* Actions */}
            {editing ? (
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setModalEditing(false)} style={{
                  flex: 1, padding: '12px', fontSize: '12px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500',
                  background: 'transparent', border: `1px solid ${border}`, borderRadius: '10px', color: c.muted, cursor: 'pointer',
                }}>Cancel</button>
                <button style={{
                  flex: 2, padding: '12px', fontSize: '12px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500',
                  background: c.accent, border: 'none', borderRadius: '10px',
                  color: isLight ? '#F5F5F0' : '#1A1A1A', cursor: 'pointer', letterSpacing: '0.03em',
                }}>Save Changes</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => openEventEditing(ev)} style={{
                  flex: 1, padding: '12px', fontSize: '12px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500',
                  background: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'}`,
                  borderRadius: '10px', color: c.text, cursor: 'pointer', transition: 'all 0.2s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  Edit
                </button>
                <button style={{
                  flex: 1, padding: '12px', fontSize: '12px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500',
                  background: 'rgba(220,60,60,0.06)', border: '1px solid rgba(220,60,60,0.15)',
                  borderRadius: '10px', color: '#C44', cursor: 'pointer', transition: 'all 0.2s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                  </svg>
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // =========================================================================
  // DAY DETAIL MODAL (Month view — shows all events like Overview)
  // =========================================================================
  const renderDayDetailModal = () => {
    if (showDayDetail === null) return null;
    const dayNum = showDayDetail;
    const dayEvents = getEventsForDate(currentYear, currentMonth, dayNum);
    const dayName = DAY_NAMES_FULL[new Date(currentYear, currentMonth, dayNum).getDay()];

    return (
      <div onClick={() => { setShowDayDetail(null); setExpandedDayEvent(null); }} style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: isLight ? 'rgba(245,245,240,0.88)' : 'rgba(0,0,0,0.75)',
        backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'calFadeInSimple 0.3s ease',
      }}>
        <div onClick={e => e.stopPropagation()} style={{
          width: '540px', maxWidth: '90vw', maxHeight: '80vh',
          background: isLight ? c.cardBg : '#1E1E1E',
          border: `1px solid ${border}`, borderRadius: '20px',
          boxShadow: isLight ? '0 32px 80px rgba(0,0,0,0.1)' : `0 32px 80px rgba(0,0,0,0.5), 0 0 40px ${c.accentGlow}`,
          animation: 'calScaleIn 0.4s cubic-bezier(0.4,0,0.2,1)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            padding: '28px 32px 20px', flexShrink: 0,
            borderBottom: `1px solid ${border}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          }}>
            <div>
              <div style={{ fontSize: '11px', fontFamily: '"DM Sans", sans-serif', color: c.muted, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '6px' }}>
                {dayName}
              </div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                <span style={{ fontSize: '36px', fontWeight: '300', color: c.text, fontFamily: '"Cormorant Garamond", serif', lineHeight: 1 }}>
                  {dayNum}
                </span>
                <span style={{ fontSize: '18px', fontWeight: '300', fontStyle: 'italic', color: c.muted, fontFamily: '"Cormorant Garamond", serif' }}>
                  {monthName}
                </span>
              </div>
              {dayEvents.length > 0 && (
                <div style={{ fontSize: '12px', fontFamily: '"DM Sans", sans-serif', color: c.muted, marginTop: '6px' }}>
                  {dayEvents.length} event{dayEvents.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button onClick={() => {
                setShowDayDetail(null);
                setExpandedDayEvent(null);
                setModalDate(dateKey(currentYear, currentMonth, dayNum));
                setModalStartTime('09:00');
                setModalEndTime('10:00');
                setSelectedColor('#667eea');
                setShowModal(true);
              }} style={{
                padding: '8px 16px', fontSize: '12px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500',
                background: c.accent, border: 'none', borderRadius: '8px',
                color: isLight ? '#F5F5F0' : '#1A1A1A', cursor: 'pointer',
              }}>+ New Event</button>
              <button onClick={() => { setShowDayDetail(null); setExpandedDayEvent(null); }} style={{
                width: '32px', height: '32px', borderRadius: '50%',
                background: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)',
                border: 'none', color: c.muted, cursor: 'pointer', fontSize: '18px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>×</button>
            </div>
          </div>

          {/* Event list — scrollable */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '20px 32px 28px',
          }}>
            {dayEvents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{ fontSize: '18px', fontWeight: '300', fontStyle: 'italic', color: c.muted, fontFamily: '"Cormorant Garamond", serif', marginBottom: '8px' }}>
                  Nothing scheduled
                </div>
                <div style={{ fontSize: '12px', fontFamily: '"DM Sans", sans-serif', color: c.muted, opacity: 0.5 }}>
                  Click "+ New Event" to add one
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {dayEvents.map((ev, i) => {
                  const isExp = expandedDayEvent === i;
                  return (
                    <div key={i}
                      onClick={() => setExpandedDayEvent(isExp ? null : i)}
                      style={{
                        background: isExp ? (isLight ? '#FAFAF6' : 'rgba(255,255,255,0.04)') : 'transparent',
                        border: `1px solid ${isExp ? `${c.accent}30` : border}`,
                        borderLeft: `3px solid ${ev.color}`,
                        borderRadius: '12px', cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                        overflow: 'hidden',
                      }}
                    >
                      {/* Collapsed */}
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '14px 18px',
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                          <div style={{
                            width: '8px', height: '8px', borderRadius: '50%', background: ev.color, flexShrink: 0,
                            boxShadow: isExp ? `0 0 6px ${ev.color}60` : 'none', transition: 'box-shadow 0.3s',
                          }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: '14px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500', color: c.text,
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>{ev.title}</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                          <span style={{ fontSize: '11px', fontFamily: '"DM Sans", sans-serif', color: c.muted, fontWeight: '500' }}>
                            {ev.time} — {ev.endTime}
                          </span>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={c.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                            style={{ transition: 'transform 0.3s ease', transform: isExp ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </div>
                      </div>

                      {/* Expanded */}
                      <div style={{
                        maxHeight: isExp ? '350px' : '0', opacity: isExp ? 1 : 0,
                        transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)', overflow: 'hidden',
                      }}>
                        <div style={{
                          padding: '0 18px 16px',
                          borderTop: `1px solid ${isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)'}`,
                          paddingTop: '14px',
                        }}>
                          <p style={{ fontSize: '13px', fontFamily: '"DM Sans", sans-serif', color: c.textSecondary, lineHeight: 1.7, margin: '0 0 14px 0' }}>{ev.desc}</p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
                            <div style={{
                              display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '16px',
                              background: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)',
                              fontSize: '10px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500', color: c.muted,
                            }}>
                              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                              {ev.time} — {ev.endTime}
                            </div>
                            {ev.course && (
                              <div style={{
                                display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '16px',
                                background: `${ev.color}15`, fontSize: '10px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500',
                                color: isLight ? ev.color : c.text,
                              }}>
                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" /><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" /></svg>
                                {ev.course}
                              </div>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button onClick={(e) => {
                              e.stopPropagation();
                              setShowDayDetail(null);
                              setExpandedDayEvent(null);
                              const evData = { ...ev, _day: dayNum };
                              setViewingEvent(evData);
                              openEventEditing(evData);
                            }} style={{
                              flex: 1, padding: '9px', fontSize: '11px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500',
                              background: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)',
                              border: `1px solid ${isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'}`,
                              borderRadius: '8px', color: c.text, cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                            }}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                              Edit
                            </button>
                            <button onClick={(e) => e.stopPropagation()} style={{
                              padding: '9px 12px', fontSize: '11px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500',
                              background: 'rgba(220,60,60,0.06)', border: '1px solid rgba(220,60,60,0.15)',
                              borderRadius: '8px', color: '#C44', cursor: 'pointer',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                            }}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                              </svg>
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSendScheduleModal = () => {
    if (!showSendSchedule) return null;
    return (
      <div onClick={() => { setShowSendSchedule(false); setSelectedContacts([]); setSendConfirmed(false); setSendFormat('ics'); }} style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: isLight ? 'rgba(245,245,240,0.88)' : 'rgba(0,0,0,0.75)',
        backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'calFadeInSimple 0.3s ease',
      }}>
        <div onClick={e => e.stopPropagation()} style={{
          width: '480px', maxWidth: '90vw', background: isLight ? c.cardBg : '#1E1E1E',
          border: `1px solid ${border}`, borderRadius: '20px', padding: '36px',
          boxShadow: isLight ? '0 32px 80px rgba(0,0,0,0.1)' : `0 32px 80px rgba(0,0,0,0.5), 0 0 40px ${c.accentGlow}`,
          animation: 'calScaleIn 0.4s cubic-bezier(0.4,0,0.2,1)',
        }}>
          {/* Confirmed State */}
          {sendConfirmed ? (
            <div style={{
              textAlign: 'center', padding: '24px 0',
              animation: 'calFadeIn 0.4s ease',
            }}>
              <div style={{
                width: '56px', height: '56px', borderRadius: '50%', margin: '0 auto 20px',
                background: `${c.accent}15`, border: `2px solid ${c.accent}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={c.accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
              <div style={{ fontSize: '22px', fontWeight: '300', color: c.text, fontFamily: '"Cormorant Garamond", serif', marginBottom: '8px' }}>
                Schedule Sent
              </div>
              <div style={{ fontSize: '13px', fontFamily: '"DM Sans", sans-serif', color: c.muted }}>
                {selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''} will receive your schedule as {sendFormat === 'ics' ? 'a calendar file' : 'plain text'}
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' }}>
                <div>
                  <div style={{ fontSize: '11px', fontFamily: '"DM Sans", sans-serif', color: c.muted, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '6px' }}>
                    Send Schedule
                  </div>
                  <div style={{ fontSize: '24px', fontWeight: '300', color: c.text, fontFamily: '"Cormorant Garamond", serif' }}>
                    Share your {VIEW_LABELS[view].toLowerCase()} view
                  </div>
                </div>
                <button onClick={() => { setShowSendSchedule(false); setSelectedContacts([]); setSendFormat('ics'); }} style={{
                  width: '32px', height: '32px', borderRadius: '50%',
                  background: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)',
                  border: 'none', color: c.muted, cursor: 'pointer', fontSize: '18px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>×</button>
              </div>

              {/* Format Toggle */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '10px', fontFamily: '"DM Sans", sans-serif', color: c.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '10px' }}>
                  Format
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[
                    { id: 'ics', label: 'Calendar File', sublabel: '.ics — importable', icon: (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                    )},
                    { id: 'text', label: 'Plain Text', sublabel: 'Readable list', icon: (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="17" y1="10" x2="3" y2="10" /><line x1="21" y1="6" x2="3" y2="6" /><line x1="21" y1="14" x2="3" y2="14" /><line x1="17" y1="18" x2="3" y2="18" />
                      </svg>
                    )},
                  ].map(fmt => {
                    const isActive = sendFormat === fmt.id;
                    return (
                      <button key={fmt.id} onClick={() => setSendFormat(fmt.id)} style={{
                        flex: 1, display: 'flex', alignItems: 'center', gap: '10px',
                        padding: '12px 14px', borderRadius: '10px',
                        background: isActive
                          ? (isLight ? 'rgba(139,139,122,0.08)' : `${c.accent}10`)
                          : (isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.02)'),
                        border: `1px solid ${isActive ? `${c.accent}50` : (isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)')}`,
                        cursor: 'pointer', transition: 'all 0.2s ease',
                        color: isActive ? c.accent : c.muted,
                      }}>
                        {fmt.icon}
                        <div style={{ textAlign: 'left' }}>
                          <div style={{ fontSize: '13px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500', color: isActive ? c.text : c.muted }}>{fmt.label}</div>
                          <div style={{ fontSize: '10px', fontFamily: '"DM Sans", sans-serif', color: c.muted, marginTop: '1px' }}>{fmt.sublabel}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Contact List */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ fontSize: '10px', fontFamily: '"DM Sans", sans-serif', color: c.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '12px' }}>
                  Select Recipients
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {CONTACTS.map((contact, i) => {
                    const isSelected = selectedContacts.includes(contact.id);
                    return (
                      <div key={contact.id}
                        onClick={() => toggleContact(contact.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '14px',
                          padding: '14px 16px', borderRadius: '12px',
                          background: isSelected
                            ? (isLight ? `rgba(139,139,122,0.08)` : `${c.accent}10`)
                            : (isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)'),
                          border: `1px solid ${isSelected ? `${c.accent}40` : (isLight ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)')}`,
                          cursor: 'pointer', transition: 'all 0.25s ease',
                          animation: `calFadeIn 0.3s ease ${i * 0.05}s both`,
                        }}
                      >
                        {/* Avatar */}
                        <div style={{
                          width: '38px', height: '38px', borderRadius: '50%', flexShrink: 0,
                          background: `linear-gradient(135deg, ${contact.color}40, ${contact.color}20)`,
                          border: `1px solid ${contact.color}30`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '12px', fontFamily: '"DM Sans", sans-serif', fontWeight: '600',
                          color: isLight ? contact.color : c.text,
                        }}>
                          {contact.initials}
                        </div>

                        {/* Info */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '14px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500', color: c.text }}>{contact.name}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '2px' }}>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={c.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              {contact.method === 'phone' ? (
                                <><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/></>
                              ) : (
                                <><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></>
                              )}
                            </svg>
                            <span style={{ fontSize: '11px', fontFamily: '"DM Sans", sans-serif', color: c.muted }}>{contact.detail}</span>
                          </div>
                        </div>

                        {/* Checkbox */}
                        <div style={{
                          width: '22px', height: '22px', borderRadius: '6px', flexShrink: 0,
                          border: `2px solid ${isSelected ? c.accent : (isLight ? 'rgba(0,0,0,0.15)' : 'rgba(255,255,255,0.15)')}`,
                          background: isSelected ? c.accent : 'transparent',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'all 0.2s ease',
                        }}>
                          {isSelected && (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={isLight ? '#F5F5F0' : '#1A1A1A'} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Method selector note */}
              {selectedContacts.length > 0 && (
                <div style={{
                  padding: '12px 16px', marginBottom: '20px', borderRadius: '10px',
                  background: isLight ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)'}`,
                  fontSize: '12px', fontFamily: '"DM Sans", sans-serif', color: c.muted,
                  display: 'flex', alignItems: 'center', gap: '8px',
                  animation: 'calFadeIn 0.3s ease',
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  Schedule will be sent as {sendFormat === 'ics' ? 'an importable .ics calendar file' : 'a plain text event list'} via each contact's preferred method
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button onClick={() => { setShowSendSchedule(false); setSelectedContacts([]); setSendFormat('ics'); }} style={{
                  flex: 1, padding: '13px', fontSize: '13px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500',
                  background: 'transparent', border: `1px solid ${border}`, borderRadius: '10px',
                  color: c.muted, cursor: 'pointer',
                }}>Cancel</button>
                <button onClick={handleSendSchedule} disabled={selectedContacts.length === 0} style={{
                  flex: 2, padding: '13px', fontSize: '13px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500',
                  background: selectedContacts.length > 0 ? c.accent : (isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'),
                  border: 'none', borderRadius: '10px',
                  color: selectedContacts.length > 0 ? (isLight ? '#F5F5F0' : '#1A1A1A') : c.muted,
                  cursor: selectedContacts.length > 0 ? 'pointer' : 'default',
                  transition: 'all 0.2s ease', letterSpacing: '0.03em',
                }}>
                  Send to {selectedContacts.length || '...'} contact{selectedContacts.length !== 1 ? 's' : ''}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // =========================================================================
  // SHARED: EVENT CREATION MODAL (frosted glass — for Month/Week/Agenda)
  // =========================================================================
  const renderModal = () => {
    if (!showModal) return null;
    return (
      <div onClick={() => setShowModal(false)} style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: isLight ? 'rgba(245,245,240,0.85)' : 'rgba(0,0,0,0.7)',
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'calFadeInSimple 0.3s ease',
      }}>
        <div onClick={e => e.stopPropagation()} style={{
          width: '420px', maxWidth: '90vw', background: c.cardBg,
          border: `1px solid ${border}`, borderRadius: '16px', padding: '36px',
          boxShadow: isLight ? '0 24px 80px rgba(0,0,0,0.08)' : '0 24px 80px rgba(0,0,0,0.4)',
          animation: 'calScaleIn 0.4s cubic-bezier(0.4,0,0.2,1)',
        }}>
          <div style={{ fontSize: '11px', fontFamily: '"DM Sans", sans-serif', color: c.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>New Event</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '24px' }}>
            <input type="date" value={modalDate} onChange={e => setModalDate(e.target.value)} style={{
              fontSize: '24px', fontWeight: '300', color: c.text, fontFamily: '"Cormorant Garamond", serif',
              background: 'transparent', border: 'none', outline: 'none',
              borderBottom: `1px solid ${isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'}`,
              padding: '4px 0', cursor: 'pointer',
              colorScheme: isLight ? 'light' : 'dark',
            }} />
          </div>
          <input placeholder="Event title" style={{
            width: '100%', padding: '14px 0', fontSize: '18px', fontWeight: '300',
            background: 'transparent', border: 'none', color: c.text,
            borderBottom: `1px solid ${isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}`,
            outline: 'none', fontFamily: '"Cormorant Garamond", serif', marginBottom: '20px',
          }} />
          <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
            {['Start', 'End'].map((label, i) => (
              <div key={label} style={{ flex: 1 }}>
                <label style={{ fontSize: '10px', fontFamily: '"DM Sans", sans-serif', color: c.muted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>{label}</label>
                <input type="time" value={i === 0 ? modalStartTime : modalEndTime}
                  onChange={e => i === 0 ? setModalStartTime(e.target.value) : setModalEndTime(e.target.value)}
                  style={{
                  width: '100%', padding: '10px 12px', fontSize: '13px', fontFamily: '"DM Sans", sans-serif',
                  background: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${border}`, borderRadius: '8px', color: c.text, outline: 'none',
                }} />
              </div>
            ))}
          </div>
          <textarea placeholder="Description (optional)" rows={3} style={{
            width: '100%', padding: '12px', fontSize: '13px', fontFamily: '"DM Sans", sans-serif',
            background: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.05)',
            border: `1px solid ${border}`, borderRadius: '8px', color: c.text, outline: 'none', resize: 'none', marginBottom: '20px',
          }} />
          {/* Color Picker */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ fontSize: '10px', fontFamily: '"DM Sans", sans-serif', color: c.muted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '10px' }}>Color</label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {EVENT_COLORS.map(clr => {
                const isActive = selectedColor === clr.hex;
                return (
                  <button key={clr.hex} onClick={(e) => { e.stopPropagation(); setSelectedColor(clr.hex); }}
                    title={clr.name}
                    style={{
                      width: '32px', height: '32px', borderRadius: '50%', border: 'none', cursor: 'pointer',
                      background: clr.hex, position: 'relative',
                      boxShadow: isActive ? `0 0 0 2px ${c.bg}, 0 0 0 4px ${clr.hex}` : `0 2px 6px ${clr.hex}30`,
                      transform: isActive ? 'scale(1.1)' : 'scale(1)',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {isActive && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }}>
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button onClick={() => setShowModal(false)} style={{
              padding: '10px 24px', fontSize: '13px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500',
              background: 'transparent', border: `1px solid ${border}`, borderRadius: '8px', color: c.muted, cursor: 'pointer',
            }}>Cancel</button>
            <button style={{
              padding: '10px 28px', fontSize: '13px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500',
              background: c.accent, border: 'none', borderRadius: '8px',
              color: isLight ? '#F5F5F0' : '#1A1A1A', cursor: 'pointer',
            }}>Create</button>
          </div>
        </div>
      </div>
    );
  };

  // =========================================================================
  // SHARED: SLIDE-IN PANEL (for Overview only)
  // =========================================================================
  const renderSlidePanel = () => {
    if (!showSlidePanel) return null;
    return (
      <>
        <div onClick={() => setShowSlidePanel(false)} style={{
          position: 'fixed', inset: 0, zIndex: 99, background: 'rgba(0,0,0,0.3)',
          animation: 'calFadeInSimple 0.3s ease',
        }} />
        <div style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: '400px', maxWidth: '90vw',
          zIndex: 100, background: isLight ? '#FBFBF8' : '#1A1A1A',
          borderLeft: `1px solid ${border}`, padding: '40px 32px',
          boxShadow: '-20px 0 60px rgba(0,0,0,0.15)',
          animation: 'calSlideIn 0.4s cubic-bezier(0.4,0,0.2,1)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '36px' }}>
            <span style={{ fontSize: '11px', fontFamily: '"DM Sans", sans-serif', fontWeight: '600', color: c.muted, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Create Event</span>
            <button onClick={() => setShowSlidePanel(false)} style={{ background: 'none', border: 'none', color: c.muted, cursor: 'pointer', fontSize: '22px', lineHeight: 1 }}>×</button>
          </div>
          <div style={{ fontSize: '24px', fontWeight: '300', color: c.text, marginBottom: '28px', fontFamily: '"Cormorant Garamond", serif' }}>
            <input type="date" value={modalDate} onChange={e => setModalDate(e.target.value)} style={{
              fontSize: '20px', fontWeight: '300', color: c.text, fontFamily: '"Cormorant Garamond", serif',
              background: 'transparent', border: 'none', outline: 'none',
              borderBottom: `1px solid ${isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'}`,
              padding: '4px 0', cursor: 'pointer', width: '100%',
              colorScheme: isLight ? 'light' : 'dark',
            }} />
          </div>
          <input placeholder="Event title" style={{
            width: '100%', padding: '14px 0', fontSize: '20px', fontWeight: '300',
            background: 'transparent', border: 'none', color: c.text,
            borderBottom: `1px solid ${isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}`,
            outline: 'none', fontFamily: '"Cormorant Garamond", serif', marginBottom: '24px',
          }} />
          <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
            {['Start', 'End'].map((label, i) => (
              <div key={label} style={{ flex: 1 }}>
                <label style={{ fontSize: '10px', fontFamily: '"DM Sans", sans-serif', color: c.muted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>{label}</label>
                <input type="time" value={i === 0 ? modalStartTime : modalEndTime}
                  onChange={e => i === 0 ? setModalStartTime(e.target.value) : setModalEndTime(e.target.value)}
                  style={{
                  width: '100%', padding: '10px 12px', fontSize: '13px', fontFamily: '"DM Sans", sans-serif',
                  background: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${border}`, borderRadius: '8px', color: c.text, outline: 'none',
                }} />
              </div>
            ))}
          </div>
          <label style={{ fontSize: '10px', fontFamily: '"DM Sans", sans-serif', color: c.muted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '6px' }}>Description</label>
          <textarea placeholder="Add details..." rows={4} style={{
            width: '100%', padding: '12px', fontSize: '13px', fontFamily: '"DM Sans", sans-serif',
            background: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.05)',
            border: `1px solid ${border}`, borderRadius: '8px', color: c.text, outline: 'none', resize: 'none', marginBottom: '24px',
          }} />
          {/* Color Picker */}
          <div style={{ marginBottom: '28px' }}>
            <label style={{ fontSize: '10px', fontFamily: '"DM Sans", sans-serif', color: c.muted, letterSpacing: '0.08em', textTransform: 'uppercase', display: 'block', marginBottom: '10px' }}>Color</label>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {EVENT_COLORS.map(clr => {
                const isActive = selectedColor === clr.hex;
                return (
                  <button key={clr.hex} onClick={() => setSelectedColor(clr.hex)}
                    title={clr.name}
                    style={{
                      width: '30px', height: '30px', borderRadius: '50%', border: 'none', cursor: 'pointer',
                      background: clr.hex, position: 'relative',
                      boxShadow: isActive ? `0 0 0 2px ${isLight ? '#FBFBF8' : '#1A1A1A'}, 0 0 0 4px ${clr.hex}` : `0 2px 6px ${clr.hex}30`,
                      transform: isActive ? 'scale(1.1)' : 'scale(1)',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {isActive && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }}>
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <button style={{
            width: '100%', padding: '14px', fontSize: '13px', fontFamily: '"DM Sans", sans-serif',
            fontWeight: '500', background: c.accent, border: 'none', borderRadius: '10px',
            color: isLight ? '#F5F5F0' : '#1A1A1A', cursor: 'pointer', letterSpacing: '0.04em',
          }}>Create Event</button>
        </div>
      </>
    );
  };

  // =========================================================================
  // VIEW: MONTH (Editorial — production rewrite)
  // =========================================================================
  const MONTH_CELL_HEIGHT = 120;
  const MAX_VISIBLE_EVENTS = 3;

  const renderMonth = () => {
    const numRows = Math.ceil(monthCells.length / 7);

    return (
      <div key={`${viewKey}-${currentMonth}`} style={{ animation: 'calFadeIn 0.5s ease' }}>
        {/* Day-of-week header row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          borderBottom: `1px solid ${border}`,
        }}>
          {DAYS_SHORT.map(d => (
            <div key={d} style={{
              textAlign: 'center',
              padding: '12px 0',
              fontSize: '11px',
              fontFamily: '"DM Sans", sans-serif',
              fontWeight: '500',
              color: c.muted,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}>{d}</div>
          ))}
        </div>

        {/* Calendar grid — fixed row heights */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gridTemplateRows: `repeat(${numRows}, ${MONTH_CELL_HEIGHT}px)`,
          border: `1px solid ${border}`,
          borderTop: 'none',
          borderRadius: '0 0 16px 16px',
          overflow: 'hidden',
        }}>
          {monthCells.map((day, cellIdx) => {
            const events = day ? getEventsForDate(currentYear, currentMonth, day) : [];
            const isToday = day === todayDay;
            const isHovered = hoveredDay === day && day !== null;
            const hiddenCount = Math.max(0, events.length - MAX_VISIBLE_EVENTS);
            const visibleEvents = events.slice(0, MAX_VISIBLE_EVENTS);

            return (
              <div
                key={cellIdx}
                onMouseEnter={() => day && setHoveredDay(day)}
                onMouseLeave={() => setHoveredDay(null)}
                onClick={() => {
                  if (!day) return;
                  setExpandedDayEvent(null);
                  setShowDayDetail(day);
                }}
                style={{
                  height: `${MONTH_CELL_HEIGHT}px`,
                  overflow: 'hidden',
                  padding: '6px 5px',
                  background: isHovered && day
                    ? (isLight ? '#FAFAF6' : 'rgba(255,255,255,0.02)')
                    : c.bg,
                  borderRight: (cellIdx + 1) % 7 !== 0 ? `1px solid ${border}` : 'none',
                  borderBottom: cellIdx < monthCells.length - 7 ? `1px solid ${border}` : 'none',
                  cursor: day ? 'pointer' : 'default',
                  opacity: day ? 1 : 0.4,
                  transition: 'background 0.15s ease',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {day !== null && (
                  <>
                    {/* Day number — top left aligned */}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      marginBottom: '4px',
                      flexShrink: 0,
                    }}>
                      <span style={{
                        width: '28px',
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: isToday ? '6px' : '50%',
                        fontSize: '13px',
                        fontWeight: isToday ? '600' : '400',
                        fontFamily: '"DM Sans", sans-serif',
                        color: c.text,
                        background: 'transparent',
                        border: isToday ? `1.5px solid ${isLight ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.25)'}` : '1.5px solid transparent',
                        lineHeight: 1,
                      }}>{day}</span>
                    </div>

                    {/* Event pills — fixed max count */}
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                      flex: 1,
                      overflow: 'hidden',
                    }}>
                      {visibleEvents.map((ev, evIdx) => (
                        <div
                          key={evIdx}
                          style={{
                            height: '20px',
                            flexShrink: 0,
                            padding: '2px 6px',
                            borderRadius: '3px',
                            background: `${ev.color}${isLight ? '15' : '22'}`,
                            borderLeft: `2.5px solid ${ev.color}`,
                            fontSize: '10px',
                            fontFamily: '"DM Sans", sans-serif',
                            fontWeight: '500',
                            color: c.text,
                            lineHeight: '16px',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {ev.title}
                        </div>
                      ))}

                      {/* "+N more" indicator */}
                      {hiddenCount > 0 && (
                        <div style={{
                          height: '18px',
                          flexShrink: 0,
                          padding: '1px 6px',
                          fontSize: '10px',
                          fontFamily: '"DM Sans", sans-serif',
                          fontWeight: '500',
                          color: c.accent,
                          lineHeight: '16px',
                          cursor: 'pointer',
                        }}>
                          +{hiddenCount} more
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // =========================================================================
  // VIEW: WEEK (Editorial — with clickable half-hour slots)
  // =========================================================================
  const WEEK_START_HOUR = 7;
  const WEEK_END_HOUR = 19;
  const WEEK_HOURS = WEEK_END_HOUR - WEEK_START_HOUR;
  const ROW_HEIGHT = 60; // px per hour
  const HALF_HEIGHT = ROW_HEIGHT / 2;

  const [hoveredSlot, setHoveredSlot] = useState(null);

  const renderWeek = () => {
    const weekDates = getWeekDates();
    const weekEvents = getWeekEventsFromDate(weekStart);
    return (
    <div key={`${viewKey}-${weekStart.getTime()}`} style={{ animation: 'calFadeIn 0.5s ease' }}>
      {/* Week Navigation */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: '16px', padding: '0 4px',
      }}>
        <button onClick={() => navigateWeek(-1)} style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '8px 16px', borderRadius: '8px',
          background: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)',
          border: `1px solid ${isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}`,
          fontSize: '12px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500',
          color: c.text, cursor: 'pointer', transition: 'all 0.2s ease',
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Prev Week
        </button>
        <div style={{
          fontSize: '14px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500', color: c.text,
        }}>
          {formatWeekRange()}
        </div>
        <button onClick={() => navigateWeek(1)} style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '8px 16px', borderRadius: '8px',
          background: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)',
          border: `1px solid ${isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}`,
          fontSize: '12px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500',
          color: c.text, cursor: 'pointer', transition: 'all 0.2s ease',
        }}>
          Next Week
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      </div>

      {/* Header Row */}
      <div style={{
        display: 'grid', gridTemplateColumns: '60px repeat(7, 1fr)',
        border: `1px solid ${border}`, borderBottom: 'none',
        borderRadius: '16px 16px 0 0', overflow: 'hidden',
      }}>
        <div style={{ background: c.bgAlt, padding: '16px 8px' }} />
        {weekDates.map((wd, i) => {
          const dayName = DAYS_SHORT[wd.getDay()];
          const dateNum = wd.getDate();
          const isTodayCol = wd.getFullYear() === 2026 && wd.getMonth() === 2 && dateNum === 15;
          return (
            <div key={`${dayName}-${wd.getTime()}`}
              style={{
                textAlign: 'center', padding: '12px 4px', background: c.bgAlt,
                borderLeft: `1px solid ${isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)'}`,
              }}>
              <div style={{ fontSize: '10px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500', color: isTodayCol ? c.text : c.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>{dayName}</div>
              <div style={{
                fontSize: '22px', fontWeight: '300', fontFamily: '"Cormorant Garamond", serif',
                color: c.text,
                width: '36px', height: '36px',
                borderRadius: isTodayCol ? '8px' : '50%',
                background: 'transparent',
                border: isTodayCol ? `1.5px solid ${isLight ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.25)'}` : '1.5px solid transparent',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>{dateNum}</div>
            </div>
          );
        })}
      </div>

      {/* Time Grid Body */}
      <div style={{
        display: 'grid', gridTemplateColumns: '60px repeat(7, 1fr)',
        border: `1px solid ${border}`, borderTop: `1px solid ${border}`,
        borderRadius: '0 0 16px 16px', overflow: 'hidden',
      }}>
        {/* Time Gutter */}
        <div style={{ position: 'relative', height: `${WEEK_HOURS * ROW_HEIGHT}px` }}>
          {Array.from({ length: WEEK_HOURS }, (_, i) => {
            const hour = WEEK_START_HOUR + i;
            return (
              <div key={hour} style={{
                position: 'absolute', top: `${i * ROW_HEIGHT}px`, right: '8px',
                fontSize: '10px', fontFamily: '"DM Sans", sans-serif',
                color: c.muted, lineHeight: 1,
              }}>
                {hour > 12 ? `${hour - 12}p` : hour === 12 ? '12p' : `${hour}a`}
              </div>
            );
          })}
        </div>

        {/* Day Columns */}
        {Array.from({ length: 7 }, (_, dayIdx) => {
          const dayEvents = weekEvents.filter(e => e.day === dayIdx);
          return (
            <div key={dayIdx} style={{
              position: 'relative', height: `${WEEK_HOURS * ROW_HEIGHT}px`,
              borderLeft: `1px solid ${isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)'}`,
            }}>
              {/* Hour grid lines */}
              {Array.from({ length: WEEK_HOURS }, (_, i) => (
                <div key={`line-${i}`} style={{
                  position: 'absolute', top: `${i * ROW_HEIGHT}px`, left: 0, right: 0,
                  borderBottom: `1px solid ${isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)'}`,
                }} />
              ))}
              {/* Half-hour dashed lines */}
              {Array.from({ length: WEEK_HOURS }, (_, i) => (
                <div key={`half-${i}`} style={{
                  position: 'absolute', top: `${i * ROW_HEIGHT + HALF_HEIGHT}px`, left: 0, right: 0,
                  borderBottom: `1px dashed ${isLight ? 'rgba(0,0,0,0.025)' : 'rgba(255,255,255,0.025)'}`,
                }} />
              ))}

              {/* Clickable half-hour slots */}
              {Array.from({ length: WEEK_HOURS * 2 }, (_, i) => {
                const slotHour = WEEK_START_HOUR + i * 0.5;
                const slotKey = `${dayIdx}-${slotHour}`;
                const isHov = hoveredSlot === slotKey;
                return (
                  <div key={slotKey}
                    onMouseEnter={() => setHoveredSlot(slotKey)}
                    onMouseLeave={() => setHoveredSlot(null)}
                    onClick={(e) => { e.stopPropagation(); openModalWithTime(dayIdx, slotHour); }}
                    style={{
                      position: 'absolute',
                      top: `${i * HALF_HEIGHT}px`,
                      left: 0, right: 0, height: `${HALF_HEIGHT}px`,
                      cursor: 'pointer', zIndex: 1,
                      background: isHov ? (isLight ? 'rgba(139,139,122,0.06)' : `${c.accent}08`) : 'transparent',
                      transition: 'background 0.15s ease',
                    }}
                  />
                );
              })}

              {/* Events — absolutely positioned with overlap resolution */}
              {(() => {
                const resolved = resolveOverlaps(dayEvents);
                return resolved.map((event, eIdx) => {
                  const topOffset = (event.start - WEEK_START_HOUR) * ROW_HEIGHT;
                  const height = event.duration * ROW_HEIGHT;
                  const laneWidth = 100 / event.totalLanes;
                  const leftPct = event.lane * laneWidth;
                  return (
                    <div key={eIdx}
                      onClick={(e) => { e.stopPropagation(); setModalEditing(false); 
                        const evDate = weekDates[dayIdx];
                        setViewingEvent({
                        ...event,
                        time: decimalToDisplay(event.start),
                        endTime: decimalToDisplay(event.start + event.duration),
                        _day: evDate.getDate(),
                        _month: evDate.getMonth(),
                        _year: evDate.getFullYear(),
                      }); }}
                      style={{
                        position: 'absolute',
                        top: `${topOffset + 1}px`,
                        left: `calc(${leftPct}% + 2px)`,
                        width: `calc(${laneWidth}% - 4px)`,
                        height: `${height - 2}px`,
                        background: isLight ? `${event.color}18` : `${event.color}22`,
                        borderLeft: `2px solid ${event.color}`,
                        borderRadius: '4px',
                        padding: '4px 5px',
                        overflow: 'hidden',
                        fontSize: event.totalLanes > 2 ? '8px' : '10px',
                        fontFamily: '"DM Sans", sans-serif',
                        color: c.text,
                        lineHeight: 1.3,
                        cursor: 'pointer',
                        zIndex: 2,
                        transition: 'box-shadow 0.2s ease, z-index 0s',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.boxShadow = isLight ? '0 2px 8px rgba(0,0,0,0.12)' : `0 2px 12px ${event.color}40`; e.currentTarget.style.zIndex = '3'; }}
                      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.zIndex = '2'; }}
                    >
                      <div style={{ fontWeight: '500', marginBottom: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{event.title}</div>
                      {height > 35 && (
                        <div style={{ fontSize: '8px', color: c.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {decimalToDisplay(event.start)}
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          );
        })}
      </div>
    </div>
    );
  };

  // =========================================================================
  // VIEW: OVERVIEW (Daily view with mini-cal + expandable cards)
  // =========================================================================
  const renderOverview = () => (
    <div key={viewKey} style={{ display: 'flex', gap: '40px', alignItems: 'flex-start', animation: 'calFadeIn 0.5s ease' }}>
      {/* LEFT PANEL — Mini Calendar */}
      <div style={{ width: '260px', flexShrink: 0, animation: 'calFadeIn 0.6s ease 0.1s both' }}>
        {/* Mini Calendar */}
        <div style={{
          padding: '24px', background: subtleBg,
          border: `1px solid ${border}`, borderRadius: '16px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <button onClick={goToPrevMonth} style={{ background: 'none', border: 'none', color: c.muted, cursor: 'pointer', fontSize: '16px', padding: '4px' }}>‹</button>
            <span style={{ fontSize: '14px', fontWeight: '500', color: c.text, fontFamily: '"DM Sans", sans-serif' }}>{monthName} {currentYear}</span>
            <button onClick={goToNextMonth} style={{ background: 'none', border: 'none', color: c.muted, cursor: 'pointer', fontSize: '16px', padding: '4px' }}>›</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
            {DAYS_LETTER.map((d, i) => (
              <div key={i} style={{
                textAlign: 'center', fontSize: '10px', fontFamily: '"DM Sans", sans-serif',
                fontWeight: '600', color: c.muted, padding: '4px 0', letterSpacing: '0.05em',
              }}>{d}</div>
            ))}
            {monthCells.map((day, i) => {
              const hasEvent = day && getEventsForDate(currentYear, currentMonth, day).length > 0;
              const isToday = day === todayDay;
              const isSelected = day === selectedDay;
              return (
                <div key={i} onClick={() => day && setSelectedDay(day)}
                  onMouseEnter={() => day && setHoveredDay(day)}
                  onMouseLeave={() => setHoveredDay(null)}
                  style={{ textAlign: 'center', padding: '5px 0', cursor: day ? 'pointer' : 'default', position: 'relative' }}>
                  {day && (<>
                    <span style={{
                      fontSize: '12px', fontFamily: '"DM Sans", sans-serif',
                      fontWeight: isSelected || isToday ? '600' : '400',
                      color: isSelected ? (isLight ? '#F5F5F0' : '#1A1A1A') : c.text,
                      width: '28px', height: '28px',
                      borderRadius: isSelected ? '50%' : (isToday ? '6px' : '50%'),
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      background: isSelected ? c.accent : (hoveredDay === day ? subtleBgHover : 'transparent'),
                      border: isToday && !isSelected ? `1.5px solid ${isLight ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.25)'}` : '1.5px solid transparent',
                      transition: 'all 0.2s ease',
                    }}>{day}</span>
                    {hasEvent && !isSelected && (
                      <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: c.accent, margin: '2px auto 0', opacity: 0.7 }} />
                    )}
                  </>)}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* RIGHT PANEL — Daily View with Expandable Cards */}
      <div style={{ flex: 1, minWidth: 0, animation: 'calFadeIn 0.6s ease 0.15s both' }}>
        {/* Day Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
          marginBottom: '32px', paddingBottom: '20px',
          borderBottom: `1px solid ${border}`,
        }}>
          <div>
            <div style={{ fontSize: '11px', fontFamily: '"DM Sans", sans-serif', color: c.muted, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '6px' }}>
              {dayOfWeek}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
              <span style={{ fontSize: '48px', fontWeight: '300', color: c.text, fontFamily: '"Cormorant Garamond", serif', lineHeight: 1 }}>
                {selectedDay}
              </span>
              <span style={{ fontSize: '20px', fontWeight: '300', fontStyle: 'italic', color: c.muted, fontFamily: '"Cormorant Garamond", serif' }}>
                {monthName}
              </span>
            </div>
            {selectedEvents.length > 0 && (
              <div style={{ fontSize: '12px', fontFamily: '"DM Sans", sans-serif', color: c.muted, marginTop: '8px' }}>
                {selectedEvents.length} event{selectedEvents.length !== 1 ? 's' : ''} scheduled
              </div>
            )}
          </div>
          <button onClick={() => { setModalDate(dateKey(currentYear, currentMonth, selectedDay)); setModalStartTime('09:00'); setModalEndTime('10:00'); setSelectedColor('#667eea'); setShowSlidePanel(true); }} style={{
            padding: '11px 24px', fontSize: '12px', fontFamily: '"DM Sans", sans-serif',
            fontWeight: '500', background: c.accent, border: 'none', borderRadius: '8px',
            color: isLight ? '#F5F5F0' : '#1A1A1A', cursor: 'pointer', letterSpacing: '0.04em',
            transition: 'all 0.2s ease',
          }}>+ New Event</button>
        </div>

        {/* Event Cards — with overlap grouping */}
        {selectedEvents.length === 0 ? (
          <div style={{
            padding: '64px 24px', textAlign: 'center', background: subtleBg,
            borderRadius: '16px', border: `1px dashed ${isLight ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.08)'}`,
          }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '14px', margin: '0 auto 20px',
              background: isLight ? 'rgba(0,0,0,0.04)' : `${c.accent}10`,
              border: `1px solid ${isLight ? 'rgba(0,0,0,0.06)' : `${c.accent}20`}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={c.muted} strokeWidth="1.5" strokeLinecap="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </div>
            <div style={{ fontSize: '20px', fontWeight: '300', fontStyle: 'italic', color: c.muted, fontFamily: '"Cormorant Garamond", serif', marginBottom: '8px' }}>
              Nothing scheduled
            </div>
            <div style={{ fontSize: '13px', fontFamily: '"DM Sans", sans-serif', color: c.muted, opacity: 0.5 }}>
              This day is wide open
            </div>
          </div>
        ) : (
          (() => {
            // Group overlapping events into rows
            const sorted = selectedEvents.map((ev, i) => ({
              ...ev, _origIdx: i,
              _start: timeToDecimal(ev.time),
              _end: timeToDecimal(ev.endTime),
            })).sort((a, b) => a._start - b._start);

            const rows = [];
            let currentRow = [];
            let currentEnd = -1;
            for (const ev of sorted) {
              if (currentRow.length > 0 && ev._start < currentEnd) {
                currentRow.push(ev);
                currentEnd = Math.max(currentEnd, ev._end);
              } else {
                if (currentRow.length) rows.push(currentRow);
                currentRow = [ev];
                currentEnd = ev._end;
              }
            }
            if (currentRow.length) rows.push(currentRow);

            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {rows.map((row, rIdx) => (
                  <div key={rIdx} style={{
                    display: 'flex', gap: '12px',
                    animation: `calFadeIn 0.4s ease ${rIdx * 0.08}s both`,
                  }}>
                    {row.map((ev) => {
                      const i = ev._origIdx;
                      const isExpanded = expandedEvent === i;
                      const isMulti = row.length > 1;
                      return (
                        <div key={`${selectedDay}-${i}`}
                          onClick={() => setExpandedEvent(isExpanded ? null : i)}
                          style={{
                            flex: 1, minWidth: 0,
                            background: isExpanded ? (isLight ? c.cardBg : 'rgba(255,255,255,0.05)') : subtleBg,
                            border: `1px solid ${isExpanded ? `${c.accent}30` : border}`,
                            borderLeft: `3px solid ${ev.color}`,
                            borderRadius: '14px',
                            cursor: 'pointer',
                            transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: isExpanded ? (isLight ? '0 8px 32px rgba(0,0,0,0.06)' : `0 8px 32px rgba(0,0,0,0.3), 0 0 24px ${c.accentGlow}`) : 'none',
                            overflow: 'hidden',
                          }}
                        >
                          {/* Collapsed header */}
                          <div style={{
                            display: 'flex', alignItems: isMulti ? 'flex-start' : 'center', justifyContent: 'space-between',
                            padding: isMulti ? '14px 16px' : '18px 22px',
                            flexDirection: isMulti ? 'column' : 'row', gap: isMulti ? '6px' : '0',
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: isMulti ? '10px' : '14px', minWidth: 0, width: '100%' }}>
                              <div style={{
                                width: isMulti ? '8px' : '10px', height: isMulti ? '8px' : '10px', borderRadius: '50%',
                                background: ev.color, flexShrink: 0,
                                boxShadow: isExpanded ? `0 0 8px ${ev.color}60` : 'none',
                                transition: 'box-shadow 0.3s ease',
                              }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{
                                  fontSize: isMulti ? '14px' : '16px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500',
                                  color: c.text, lineHeight: 1.3,
                                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                }}>{ev.title}</div>
                              </div>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                stroke={c.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                style={{ transition: 'transform 0.3s ease', flexShrink: 0, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                <polyline points="6 9 12 15 18 9" />
                              </svg>
                            </div>
                            <span style={{
                              fontSize: '11px', fontFamily: '"DM Sans", sans-serif', color: c.muted, fontWeight: '500',
                              paddingLeft: isMulti ? '18px' : '24px',
                            }}>{ev.time} — {ev.endTime}</span>
                          </div>

                          {/* Expanded content */}
                          <div style={{
                            maxHeight: isExpanded ? '400px' : '0', opacity: isExpanded ? 1 : 0,
                            transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)', overflow: 'hidden',
                          }}>
                            <div style={{
                              padding: isMulti ? '0 16px 16px' : '0 22px 22px',
                              borderTop: `1px solid ${isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)'}`,
                              paddingTop: '16px',
                            }}>
                              <p style={{ fontSize: '13px', fontFamily: '"DM Sans", sans-serif', color: c.textSecondary, lineHeight: 1.7, margin: '0 0 16px 0' }}>{ev.desc}</p>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '18px' }}>
                                <div style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '20px',
                                  background: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)',
                                  fontSize: '11px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500', color: c.muted,
                                }}>
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                                  {ev.time} — {ev.endTime}
                                </div>
                                {ev.course && (
                                  <div style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 12px', borderRadius: '20px',
                                    background: `${ev.color}15`, fontSize: '11px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500',
                                    color: isLight ? ev.color : c.text,
                                  }}>
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" /><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" /></svg>
                                    {ev.course}
                                  </div>
                                )}
                              </div>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button onClick={(e) => { e.stopPropagation(); const evData = { ...ev, _day: selectedDay }; setViewingEvent(evData); openEventEditing(evData); }} style={{
                                  flex: 1, padding: '10px', fontSize: '12px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500',
                                  background: isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)',
                                  border: `1px solid ${isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'}`,
                                  borderRadius: '8px', color: c.text, cursor: 'pointer', transition: 'all 0.2s',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                                }}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                                  </svg>
                                  Edit
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); }} style={{
                                  padding: '10px 14px', fontSize: '12px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500',
                                  background: 'rgba(220,60,60,0.06)', border: '1px solid rgba(220,60,60,0.15)',
                                  borderRadius: '8px', color: '#C44', cursor: 'pointer', transition: 'all 0.2s',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                                }}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                                  </svg>
                                  Delete
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            );
          })()
        )}
      </div>
    </div>
  );

  // =========================================================================
  // MAIN RENDER
  // =========================================================================
  const VIEWS = ['overview', 'week', 'month'];
  const VIEW_LABELS = { overview: 'Overview', week: 'Week', month: 'Month' };

  return (
    <div style={{
      fontFamily: '"Cormorant Garamond", Georgia, serif',
      position: 'relative', overflow: 'hidden',
      width: '100%',
    }}>

      <div style={{
        maxWidth: view === 'overview' ? '1100px' : '960px',
        margin: '0 auto', padding: '48px 32px',
        position: 'relative', zIndex: 1,
        transition: 'max-width 0.4s ease',
        animation: 'calFadeIn 0.8s cubic-bezier(0.4,0,0.2,1)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          marginBottom: '40px', flexWrap: 'wrap', gap: '16px',
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '16px' }}>
            <button onClick={() => {
              if (view === 'week') navigateWeekByMonth(-1);
              if (view === 'month') goToPrevMonth();
            }} style={{
              background: 'none', border: 'none', color: c.muted, cursor: view === 'overview' ? 'default' : 'pointer',
              fontSize: '20px', padding: '4px 8px', transition: 'all 0.2s',
              opacity: view === 'overview' ? 0.3 : 1,
              pointerEvents: view === 'overview' ? 'none' : 'auto',
            }}
              onMouseEnter={e => { if (view !== 'overview') e.currentTarget.style.color = c.text; }}
              onMouseLeave={e => { e.currentTarget.style.color = c.muted; }}
            >‹</button>
            <h1 style={{
              fontSize: '52px', fontWeight: '300', fontStyle: 'italic',
              color: c.text, letterSpacing: '-0.02em', lineHeight: 1,
            }}>{view === 'week' ? MONTHS_FULL[weekStart.getMonth()] : monthName}</h1>
            <span style={{ fontSize: '22px', fontWeight: '300', color: c.muted, letterSpacing: '-0.01em' }}>{view === 'week' ? weekStart.getFullYear() : currentYear}</span>
            <button onClick={() => {
              if (view === 'week') navigateWeekByMonth(1);
              if (view === 'month') goToNextMonth();
            }} style={{
              background: 'none', border: 'none', color: c.muted, cursor: view === 'overview' ? 'default' : 'pointer',
              fontSize: '20px', padding: '4px 8px', transition: 'all 0.2s',
              opacity: view === 'overview' ? 0.3 : 1,
              pointerEvents: view === 'overview' ? 'none' : 'auto',
            }}
              onMouseEnter={e => { if (view !== 'overview') e.currentTarget.style.color = c.text; }}
              onMouseLeave={e => { e.currentTarget.style.color = c.muted; }}
            >›</button>
          </div>

          {/* View Switcher */}
          <div style={{ display: 'flex', gap: '0px' }}>
            {VIEWS.map(v => (
              <button key={v} onClick={() => setView(v)} style={{
                background: 'none', border: 'none', padding: '8px 20px',
                fontSize: '13px', fontFamily: '"DM Sans", sans-serif', fontWeight: '500',
                color: view === v ? c.accent : c.muted,
                cursor: 'pointer', transition: 'color 0.3s ease',
                borderBottom: `2px solid ${view === v ? c.accent : 'transparent'}`,
                letterSpacing: '0.02em',
              }}>{VIEW_LABELS[v]}</button>
            ))}
          </div>
        </div>

        {/* Views */}
        {view === 'month' && renderMonth()}
        {view === 'week' && renderWeek()}
        {view === 'overview' && renderOverview()}

        {/* Send Schedule — Bottom of Page */}
        <div style={{
          marginTop: '64px', paddingTop: '32px',
          borderTop: `1px solid ${isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'}`,
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
          gap: '16px',
        }}>
          <span style={{ fontSize: '13px', fontFamily: '"DM Sans", sans-serif', color: c.muted, fontStyle: 'italic' }}>
            Share your schedule with others
          </span>
          <button onClick={() => setShowSendSchedule(true)} style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            padding: '13px 28px', borderRadius: '10px',
            background: c.accent, border: 'none', cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontFamily: '"DM Sans", sans-serif', fontSize: '13px', fontWeight: '500',
            color: isLight ? '#F5F5F0' : '#1A1A1A', letterSpacing: '0.03em',
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = `0 6px 24px ${c.accentGlow}`; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
            Send Schedule
          </button>
        </div>
      </div>

      {/* Modals/Panels */}
      {renderModal()}
      {renderSlidePanel()}
      {renderEventDetailModal()}
      {renderDayDetailModal()}
      {renderSendScheduleModal()}

      {/* Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=DM+Sans:wght@400;500;600&display=swap');
        @keyframes calFadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes calFadeInSimple { from { opacity: 0; } to { opacity: 1; } }
        @keyframes calScaleIn { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
        @keyframes calSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        * { box-sizing: border-box; margin: 0; }
        input::placeholder, textarea::placeholder { color: inherit; opacity: 0.4; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(128,128,128,0.3); border-radius: 3px; }
      `}</style>
    </div>
  );
}
